import { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Check, Loader2 } from 'lucide-react';

interface CotacaoInputPopoverProps {
  onConfirm: (numeroCotacao: string) => void;
  isLoading?: boolean;
  cotacaoAtual?: string;
  children: React.ReactNode;
}

export function CotacaoInputPopover({ onConfirm, isLoading, cotacaoAtual, children }: CotacaoInputPopoverProps) {
  const [open, setOpen] = useState(false);
  const [numeroCotacao, setNumeroCotacao] = useState('');

  const mesmaCotacao = !!cotacaoAtual && numeroCotacao.trim() === cotacaoAtual.trim();

  const handleOpenChange = (o: boolean) => {
    if (o) setNumeroCotacao('');
    setOpen(o);
  };

  const handleConfirm = () => {
    if (numeroCotacao.trim() && !mesmaCotacao) {
      onConfirm(numeroCotacao.trim());
      setNumeroCotacao('');
      setOpen(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && numeroCotacao.trim() && !mesmaCotacao) {
      handleConfirm();
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        {children}
      </PopoverTrigger>
      <PopoverContent className="w-72 p-3" align="end">
        <div className="space-y-3">
          <div className="space-y-1">
            <label className="text-sm font-medium text-foreground">
              Número da Cotação
            </label>
            <p className="text-xs text-muted-foreground">
              {cotacaoAtual ? 'Este pedido já tem cotação. Cole a NOVA cotação do Solar para substituir.' : 'Cole o número da cotação do Solar'}
            </p>
          </div>

          {cotacaoAtual && (
            <div className="rounded-md border border-border bg-muted/40 px-2 py-1.5 text-xs">
              <span className="text-muted-foreground">Cotação atual: </span>
              <span className="font-semibold tabular-nums line-through decoration-destructive/60">{cotacaoAtual}</span>
            </div>
          )}
          
          <Input
            value={numeroCotacao}
            onChange={(e) => setNumeroCotacao(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ex: COT-2025-001234"
            autoFocus
            disabled={isLoading}
          />
          
          {mesmaCotacao && (
            <p className="text-[11px] text-destructive">A nova cotação é igual à atual.</p>
          )}

          <div className="flex justify-end gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={isLoading}
            >
              Cancelar
            </Button>
            <Button
              size="sm"
              onClick={handleConfirm}
              disabled={!numeroCotacao.trim() || mesmaCotacao || isLoading}
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Check className="h-4 w-4 mr-1" />
                  {cotacaoAtual ? 'Trocar e salvar' : 'Confirmar'}
                </>
              )}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
